import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

const OTP_LENGTH = 6;
const DEFAULT_COUNTDOWN_SECONDS = 30;

export interface OtpPromptModalProps {
  open: boolean;
  title?: string;
  description?: string;
  endpoint?: string;
  countdownSeconds?: number;
  onConfirm: (code: string) => void;
  onCancel: () => void;
}

/**
 * OTP 输入弹层
 *  - 6 位数字输入，第 6 位自动提交
 *  - 30s 倒计时（超时自动取消）
 *  - Esc / 取消按钮 → onCancel
 */
export default function OtpPromptModal({
  open,
  title = "高危操作 — 请输入 OTP 验证码",
  description = "此操作涉及结算 / 资金 / 配置变更，需要二次验证。",
  endpoint,
  countdownSeconds = DEFAULT_COUNTDOWN_SECONDS,
  onConfirm,
  onCancel,
}: OtpPromptModalProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [error, setError] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 每次打开重置
  useEffect(() => {
    if (!open) return;
    setDigits(Array(OTP_LENGTH).fill(""));
    setCountdown(countdownSeconds);
    setError("");
    // 自动聚焦第一个
    const t = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open, countdownSeconds]);

  // 倒计时
  useEffect(() => {
    if (!open) return;
    if (countdown <= 0) {
      onCancel();
      return;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [open, countdown, onCancel]);

  // Esc 关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  const trySubmit = (next: string[]) => {
    const code = next.join("");
    if (code.length !== OTP_LENGTH) {
      setError(`请输入完整的 ${OTP_LENGTH} 位验证码`);
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("验证码只能包含数字");
      return;
    }
    setError("");
    onConfirm(code);
  };

  const handleChange = (index: number, value: string) => {
    // 处理粘贴整段
    if (value.length > 1) {
      const filtered = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
      const next = Array(OTP_LENGTH).fill("");
      filtered.split("").forEach((d, i) => {
        next[i] = d;
      });
      setDigits(next);
      const nextIdx = Math.min(filtered.length, OTP_LENGTH - 1);
      inputRefs.current[nextIdx]?.focus();
      if (filtered.length === OTP_LENGTH) {
        trySubmit(next);
      }
      return;
    }
    const digit = value.replace(/\D/g, "");
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (digit && index === OTP_LENGTH - 1) {
      trySubmit(next);
    }
  };

  const handleKeyDown = (index: number, e: ReactKeyboardEvent) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "Enter") {
      trySubmit(digits);
    }
  };

  const handleManualSubmit = () => {
    trySubmit(digits);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="OTP verification"
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-500/10">
            <i className="ri-shield-keyhole-line text-amber-500 text-lg" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
        </div>
        <p className="text-text-secondary text-sm leading-relaxed mb-1">{description}</p>
        {endpoint && (
          <p className="text-text-muted text-xs font-mono mb-4 break-all">{endpoint}</p>
        )}

        {/* OTP 输入 */}
        <div className="flex gap-2 sm:gap-3 justify-between mt-4">
          {Array.from({ length: OTP_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              value={digits[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              aria-label={`OTP digit ${i + 1}`}
              data-testid={`otp-input-${i}`}
              className="flex-1 h-12 rounded-xl bg-surface-card border border-surface-border text-text-primary text-lg font-semibold text-center focus:border-brand/50 focus:outline-none transition-colors min-w-0"
            />
          ))}
        </div>

        {error && (
          <p className="text-red-600 text-xs mt-3" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between mt-5">
          <span className="text-text-muted text-xs">
            {countdown > 0 ? `${countdown}s 后自动取消` : "已超时"}
          </span>
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-text-secondary border border-surface-border hover:bg-surface transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleManualSubmit}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-brand hover:bg-brand/90 transition-colors cursor-pointer"
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );
}
